import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import API from '../../../services/API'
import { BookingContext, SectionHeader } from './NewBooking'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import AltRoute from '@mui/icons-material/AltRoute'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  AvatarGroup,
  Box,
  Card,
  Paper,
  Tooltip,
} from '@mui/material'
import { Expand, ExpandMore, PersonOff } from '@mui/icons-material'
import CreateDutyForm from '../Duties/CreateDuty/CreateDutyForm'
import AssignedShifts from '../Duties/CreateDuty/AssignedShifts'
import { pickupJourneyTypes } from './PickupDropoff'
import ErrorModal from '../../../components/Modals/Error'

const Assignment = ({}) => {
  const { formData, handleChange } = useContext(BookingContext)
  const assignment = formData.assignment || {
    enabled: false,
  }
  const hasRecurrence = formData?.recurrence?.enabled
  const hasReturn = formData?.returnJourney?.required
  const isMultiJourney = hasRecurrence || hasReturn
  const firstJourneyWord = hasRecurrence ? 'first' : hasReturn ? 'outbound' : ''
  const unplannedJourneyParts = [
    hasReturn && 'the return journey',
    hasRecurrence && 'the later occurrences',
  ].filter(Boolean)
  const noTimeSet =
    !formData?.pickup?.when?.patientReadyFrom &&
    !formData?.dropoff?.when?.targetArrivalTime

  const dayOfJourney = moment(
    formData?.pickup?.when?.patientReadyFrom ||
      formData?.dropoff?.when?.targetArrivalTime,
  )

  const [matchingDuties, setMatchingDuties] = useState([])
  const [selectedDuty, setSelectedDuty] = useState(null)
  const [expandedDuties, setExpandedDuties] = useState([])
  const [disableCreateNewDuty, setDisableCreateNewDuty] = useState(false)
  const [routeError, setRouteError] = useState('')

  const isExpanded = (id) => expandedDuties.includes(id)

  const setDutyExpanded = (id, expanded) => {
    setExpandedDuties((prev) =>
      expanded ? [...new Set([...prev, id])] : prev.filter((d) => d !== id),
    )
  }

  useEffect(() => {
    if (noTimeSet) return
    if (!formData?.assignment?.enabled) return
    API.get(
      `/pts/duties-basic?date=${moment(formData?.pickup?.when?.patientReadyFrom ?? formData?.dropoff?.when?.targetArrivalTime).format('YYYYMMDD')}`,
    ).then((res) => {
      if (res.status === 200) {
        // Dedicated duties only appear for their own organisation's
        // bookings (any journey type)
        const bookingOrgId = String(
          formData?.organisation?._id || formData?.organisation || '',
        )
        setMatchingDuties(
          (res.data || []).filter((duty) => {
            if (!duty?.organisation) return true
            const dutyOrgId = String(
              duty.organisation?._id || duty.organisation,
            )
            return dutyOrgId === bookingOrgId
          }),
        )
      }
    })
  }, [
    formData?.pickup?.when?.patientReadyFrom,
    formData?.dropoff?.when?.targetArrivalTime,
    formData?.assignment,
  ])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SectionHeader
          icon={<AltRoute fontSize="small" />}
          title={
            <>
              {isMultiJourney && (
                <Box
                  component="span"
                  sx={{ textDecoration: 'underline', textTransform: 'capitalize' }}
                >
                  {firstJourneyWord}
                </Box>
              )}{' '}
              Journey Assignment
            </>
          }
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={assignment?.enabled ?? false}
              onChange={(e) => {
                handleChange('assignment', {
                  ...assignment,
                  enabled: e.target.checked,
                })
              }}
            />
          }
          label={`Plan the ${firstJourneyWord ? `${firstJourneyWord} ` : ''}journey now`}
        />
        <FormHelperText>
          Link {isMultiJourney ? `the ${firstJourneyWord}` : 'this'} journey to
          a duty now, or leave it unassigned to plan later from the control room
          or planning page.
        </FormHelperText>
      </Grid>
      {assignment?.enabled && (
        <>
          {isMultiJourney && (
            <Grid item xs={12}>
              <Alert severity="info">
                Only the {firstJourneyWord} journey will be planned onto the
                selected duty — {unplannedJourneyParts.join(' and ')} will
                appear as unassigned journeys to plan from the planning page or
                dispatch screen.
              </Alert>
            </Grid>
          )}
          {noTimeSet && (
            <Grid item xs={12}>
              <Alert severity="info">
                You need to set the booking times before planning the journey
              </Alert>
            </Grid>
          )}
          {!noTimeSet && (
            <>
              <Grid item xs={12}>
                <ToggleButtonGroup
                  value={assignment?.createNewDuty ?? 'existing'}
                  exclusive
                  onChange={(e, value) => {
                    if (!value) return
                    handleChange('assignment', {
                      ...assignment,
                      createNewDuty: value,
                    })
                  }}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      fontWeight: 100,
                    },
                  }}
                  color="contrast"
                >
                  <ToggleButton value="existing">
                    Link to Existing Duty
                  </ToggleButton>

                  <ToggleButton value="new" disabled={disableCreateNewDuty}>
                    Create New Duty
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12}>
                {assignment?.createNewDuty === 'new' ? (
                  <Card sx={{ padding: 2 }}>
                    <CreateDutyForm
                      selectedDate={dayOfJourney}
                      afterEffect={(newDuty) => {
                        setMatchingDuties((prev) => [...prev, newDuty])
                        setSelectedDuty(newDuty._id)
                        handleChange('assignment', {
                          createNewDuty: 'existing',
                          enabled: true,
                        })
                        handleChange('selectedDuty', newDuty._id)
                        setDisableCreateNewDuty(true)
                      }}
                      newFromBooking
                    />
                  </Card>
                ) : matchingDuties?.length === 0 ? (
                  <Alert
                    severity="info"
                    variant="outlined"
                    sx={{
                      borderColor: 'contrast.main',
                      color: 'contrast.main',
                    }}
                    icon={<AltRoute sx={{ color: 'contrast.main' }} />}
                  >
                    There are no duties that match the time of this booking.
                  </Alert>
                ) : (
                  <>
                    <Stack>
                      {matchingDuties.map((d) => {
                        const checked = selectedDuty === d._id
                        const expanded = isExpanded(d._id)

                        return (
                          <Accordion
                            key={d._id}
                            expanded={checked}
                            onChange={() => {
                              setSelectedDuty(checked ? null : d._id)
                              handleChange(
                                'selectedDuty',
                                checked ? null : d._id,
                              )
                            }}
                          >
                            <AccordionSummary
                              expandIcon={
                                <ExpandMore
                                  color={checked ? 'anWhite' : 'inherit'}
                                />
                              }
                              sx={{
                                backgroundColor: checked
                                  ? 'contrast.main'
                                  : 'inherit',
                              }}
                            >
                              <Stack
                                direction="row"
                                gap={1}
                                alignItems="center"
                              >
                                <Checkbox
                                  checked={checked}
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.stopPropagation()}
                                  onChange={() => {
                                    setSelectedDuty(checked ? null : d._id)
                                    handleChange(
                                      'selectedDuty',
                                      checked ? null : d._id,
                                    )

                                    if (!expanded) {
                                      setDutyExpanded(d._id, true)
                                    }
                                  }}
                                  color="anWhite"
                                />

                                <Typography
                                  color={checked ? 'white' : 'inherit'}
                                >
                                  {moment(d.start).format('Do MMM YYYY HH:mm')}{' '}
                                  -{' '}
                                  {moment(d.end).format(
                                    moment(d.start).isSame(d.end, 'day')
                                      ? 'HH:mm'
                                      : 'Do MMM YYYY HH:mm',
                                  )}
                                  {d.organisation
                                    ? ` · Dedicated crew (${d.organisation?.orgName || 'reserved'})`
                                    : ''}
                                </Typography>
                                {d?.vehicle?.callSign && (
                                  <Typography
                                    color={checked ? 'white' : 'inherit'}
                                    ml={2}
                                    fontWeight={700}
                                  >
                                    {d.vehicle.callSign}
                                  </Typography>
                                )}
                                <AvatarGroup max={4}>
                                  {d?.crewShifts?.map((x) => (
                                    <Tooltip
                                      key={x._id}
                                      title={
                                        x?.assignedTo
                                          ? x.assignedTo.userAccount.firstName +
                                            ' ' +
                                            x.assignedTo.userAccount.lastName
                                          : 'Unassigned'
                                      }
                                    >
                                      <Avatar
                                        src={
                                          x?.assignedTo?.userAccount?.photograph
                                        }
                                      >
                                        {x?.assignedTo ? (
                                          x.assignedTo.userAccount
                                            .firstName[0] +
                                          x.assignedTo.userAccount.lastName[0]
                                        ) : (
                                          <PersonOff />
                                        )}
                                      </Avatar>
                                    </Tooltip>
                                  ))}
                                </AvatarGroup>
                              </Stack>
                            </AccordionSummary>

                            <AccordionDetails>
                              <Stack gap={1}>
                                <Typography variant="title3">Shifts</Typography>
                                <AssignedShifts form={d} readOnly />
                                {d.notes && (
                                  <>
                                    <Typography variant="title3">
                                      Notes
                                    </Typography>
                                    <Typography>{d.notes}</Typography>
                                  </>
                                )}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        )
                      })}
                    </Stack>
                  </>
                )}
              </Grid>
            </>
          )}
        </>
      )}
      <ErrorModal
        open={routeError}
        onClose={() => setRouteError('')}
        title="Invalid Journey Type"
        message={routeError}
      />
    </Grid>
  )
}

export default Assignment
